import React from "react";
import { useTranslation } from "react-i18next";
import { useSnapshot } from "valtio";
import { TrashIcon } from "@aureskonnect/react-ui";
import { BiImageAdd } from "react-icons/bi";
import ReactSwitch from "react-switch";
import { SketchPicker, ColorResult } from "react-color";
import swal from "@sweetalert/with-react";

import { useDialogA11y } from "@pages/Applications/ScanToStart/ScanTo/ModalContentWrapper/shared/useDialogA11y";

import {
    designStore,
    generalConfigStore,
    setCategoryDisplay,
    setPageOrderTaking,
} from "@store";

import { resolveCategoryColor } from "@helpers/categoryColor";

import {
    CATEGORY_BORDER_STYLE_NONE,
    CATEGORY_BORDER_STYLE_SVG,
    CATEGORY_BORDER_STYLE_UNI,
    CATEGORY_OPACITY_MAX,
    CATEGORY_OPACITY_MIN,
    CATEGORY_SVG_MIME_TYPE,
    CATEGORY_SVG_FILE_EXTENSION,
    DEFAULT_CATEGORY_BORDER_COLOR,
} from "@constants/index";

// z-index du popover de couleur : au-dessus du contenu du panneau et de la
// preview. ColorPickerPopover partagé a z-index 100 ; on le surcharge ici
// localement (sans toucher au composant partagé) pour ce panneau.
// Durée (ms) d'affichage du toast swal d'erreur d'upload.
const SVG_ERROR_TOAST_MS = 2000;

// Balises dangereuses retirées d'un SVG uploadé (script + contenu HTML arbitraire).
const SVG_FORBIDDEN_TAGS = ["script", "foreignObject"];

// Purge un SVG (string XML décodée) de ses vecteurs XSS via DOMParser natif —
// PAS de dépendance externe. Retire : balises <script>/<foreignObject>, tous les
// attributs gestionnaires d'événements (on*), et les href/xlink:href en
// javascript:. Le store front est manipulable et la valeur finit dans un
// border-image CSS / peut être réutilisée ailleurs → on ne stocke jamais le SVG
// brut (OWASP A03:2021). Retourne le SVG nettoyé, ou null si invalide.
function sanitizeSvg(decoded: string): string | null {
    try {
        const doc = new DOMParser().parseFromString(
            decoded,
            "image/svg+xml"
        );
        // parseFromString crée un <parsererror> en cas de XML invalide.
        if (
            doc.getElementsByTagName("parsererror").length > 0 ||
            !doc.documentElement ||
            doc.documentElement.nodeName.toLowerCase() !== "svg"
        ) {
            return null;
        }
        for (const tag of SVG_FORBIDDEN_TAGS) {
            const nodes = doc.getElementsByTagName(tag);
            // Suppression en sens inverse (collection live).
            for (let i = nodes.length - 1; i >= 0; i -= 1) {
                nodes[i].parentNode?.removeChild(nodes[i]);
            }
        }
        const all = doc.getElementsByTagName("*");
        for (let i = 0; i < all.length; i += 1) {
            const el = all[i];
            // Copie des noms d'attributs (la collection mute pendant la boucle).
            const attrNames = el.getAttributeNames();
            for (const name of attrNames) {
                const lower = name.toLowerCase();
                const value = el.getAttribute(name) ?? "";
                const isEventHandler = lower.startsWith("on");
                const isJsHref =
                    (lower === "href" || lower === "xlink:href") &&
                    /^\s*javascript:/i.test(value);
                if (isEventHandler || isJsHref) {
                    el.removeAttribute(name);
                }
            }
        }
        return new XMLSerializer().serializeToString(doc.documentElement);
    } catch {
        return null;
    }
}

const COLOR_POPOVER_Z_INDEX = 1100;
// Décalage horizontal (px) entre la pastille et le popover (ouvert à droite).
const COLOR_POPOVER_OFFSET = 8;
// Dimensions approximatives du SketchPicker, pour garder le popover dans le
// viewport (clamp). Marge de sécurité vis-à-vis des bords.
const COLOR_POPOVER_WIDTH = 230;
const COLOR_POPOVER_HEIGHT = 320;
const COLOR_POPOVER_MARGIN = 8;

// Style commun des boutons radio "Style de bordure" : rond aligné, taille
// fixe et couleur de la marque.
// Cadre carré bordé pour les boutons d'action du cadre SVG (corbeille +
// upload), aligné sur la maquette.
const ICON_BUTTON_STYLE = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "38px",
    height: "38px",
    border: "1px solid #CECECE",
    borderRadius: "4px",
    background: "#FFFFFF",
    cursor: "pointer",
    padding: 0,
    marginBottom: 0,
} as React.CSSProperties;

const BORDER_STYLE_RADIO_STYLE = {
    marginRight: "8px",
    width: "16px",
    height: "16px",
    accentColor: DEFAULT_CATEGORY_BORDER_COLOR,
    cursor: "pointer",
    flexShrink: 0,
} as React.CSSProperties;

type ColorSwatchPropsType = {
    color: string;
    onChange: (hex: string) => void;
    testId: string;
};

function ColorSwatch({
    color,
    onChange,
    testId,
}: ColorSwatchPropsType): JSX.Element {
    const { t } = useTranslation();
    const [isPickerOpen, setIsPickerOpen] = React.useState(false);
    // Position fixe (viewport) du popover, calculée sous la pastille à
    // l'ouverture. `fixed` sort du flux du panneau scrollable : aucun scroll
    // n'apparaît dans le panneau quand le picker s'ouvre.
    const [pickerPosition, setPickerPosition] = React.useState({
        top: 0,
        left: 0,
    });
    const buttonRef = React.useRef<HTMLButtonElement>(null);
    const { dialogRef } = useDialogA11y({
        onCancel: () => setIsPickerOpen(false),
    });

    function openPicker(): void {
        const rect = buttonRef.current?.getBoundingClientRect();
        if (rect) {
            // Ouvre à droite de la pastille ; bascule à gauche si ça déborde.
            let left = rect.right + COLOR_POPOVER_OFFSET;
            if (left + COLOR_POPOVER_WIDTH > window.innerWidth) {
                left = rect.left - COLOR_POPOVER_WIDTH - COLOR_POPOVER_OFFSET;
            }
            // Aligne verticalement sur la pastille, en gardant le popover dans
            // le viewport (ne déborde ni en bas ni en haut).
            const maxTop =
                window.innerHeight -
                COLOR_POPOVER_HEIGHT -
                COLOR_POPOVER_MARGIN;
            const top = Math.max(
                COLOR_POPOVER_MARGIN,
                Math.min(rect.top, maxTop)
            );
            setPickerPosition({ top, left });
        }
        setIsPickerOpen(true);
    }

    return (
        <div
            style={{
                position: "relative",
                display: "inline-flex",
                verticalAlign: "middle",
            }}
        >
            <button
                ref={buttonRef}
                type="button"
                data-testid={testId}
                onClick={() =>
                    isPickerOpen ? setIsPickerOpen(false) : openPicker()
                }
                style={{
                    backgroundColor: color,
                    width: "28px",
                    height: "28px",
                    borderRadius: "4px",
                    border: "1px solid #CECECE",
                    cursor: "pointer",
                    padding: 0,
                    display: "block",
                }}
                aria-label={t("Color picker")}
                aria-haspopup="dialog"
                aria-expanded={isPickerOpen}
            />
            {isPickerOpen ? (
                <React.Fragment>
                    {/* Overlay plein écran : ferme au clic extérieur. */}
                    <div
                        style={{
                            position: "fixed",
                            inset: 0,
                            zIndex: COLOR_POPOVER_Z_INDEX,
                        }}
                        onClick={() => setIsPickerOpen(false)}
                    />
                    <div
                        ref={dialogRef}
                        role="dialog"
                        aria-modal="true"
                        aria-label={t("Color picker")}
                        data-testid={`${testId}-popover`}
                        style={{
                            position: "fixed",
                            top: pickerPosition.top,
                            left: pickerPosition.left,
                            zIndex: COLOR_POPOVER_Z_INDEX + 1,
                        }}
                    >
                        <SketchPicker
                            color={color}
                            onChange={(c: ColorResult) => onChange(c.hex)}
                        />
                    </div>
                </React.Fragment>
            ) : null}
        </div>
    );
}

export function CategoryDisplayMode2Settings(): JSX.Element {
    const { t } = useTranslation();
    // Id stable du libellé de groupe pour relier role=radiogroup au titre
    // (WCAG 1.3.1 : annonce de l'intitulé "Border style" par le lecteur d'écran).
    const borderStyleLabelId = React.useId();

    const {
        project: { template },
    } = useSnapshot(generalConfigStore);

    const {
        categoryDisplayBackgroundColor,
        categoryDisplayBackgroundOpacity,
        categoryDisplaySelectedTextColor,
        categoryDisplayBorderColor,
        categoryDisplaySvgBorder,
        categoryDisplaySvgBorderName,
        categoryDisplayBorderStyle,
    } = useSnapshot(designStore);

    const resolvePrimary = (color: string): string =>
        resolveCategoryColor(template.content.generalDesign, color);

    function persistOrderTaking(extra: Record<string, unknown>): void {
        setPageOrderTaking({
            ...(template.pages.ways["orderTaking"] as OrderTakingPageType)
                .orderTakingProduct,
            ...extra,
        });
    }

    function handleBackgroundColorChange(hex: string): void {
        setCategoryDisplay({ categoryDisplayBackgroundColor: hex });
        persistOrderTaking({ categoryDisplayBackgroundColor: hex });
    }

    function handleBackgroundToggle(isActive: boolean): void {
        // ON = fond opaque, OFF = fond transparent.
        const value = isActive ? CATEGORY_OPACITY_MAX : CATEGORY_OPACITY_MIN;
        setCategoryDisplay({ categoryDisplayBackgroundOpacity: value });
        persistOrderTaking({ categoryDisplayBackgroundOpacity: value });
    }

    function handleSelectedTextColorChange(hex: string): void {
        setCategoryDisplay({ categoryDisplaySelectedTextColor: hex });
        persistOrderTaking({ categoryDisplaySelectedTextColor: hex });
    }

    function handleBorderStyleChange(style: string): void {
        setCategoryDisplay({ categoryDisplayBorderStyle: style });
        persistOrderTaking({ categoryDisplayBorderStyle: style });
    }

    function handleBorderColorChange(hex: string): void {
        setCategoryDisplay({ categoryDisplayBorderColor: hex });
        persistOrderTaking({ categoryDisplayBorderColor: hex });
    }

    function showInvalidSvgError(): void {
        swal({
            icon: "error",
            content: <p>{t("File type is not valid")}</p>,
            buttons: false,
            timer: SVG_ERROR_TOAST_MS,
        });
    }

    function handleBorderSvgUpload(
        event: React.ChangeEvent<HTMLInputElement>
    ): void {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }
        const input = event.target;
        // N'accepter que des SVG : on rejette tout autre format (type + extension).
        const isSvg =
            file.type === CATEGORY_SVG_MIME_TYPE ||
            file.name.toLowerCase().endsWith(CATEGORY_SVG_FILE_EXTENSION);
        if (!isSvg) {
            input.value = "";
            showInvalidSvgError();
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            const result =
                typeof reader.result === "string" ? reader.result : "";
            // Validation du contenu réel (équivalent magic-bytes pour le SVG,
            // qui est du XML texte) : on décode la data URL base64 et la racine
            // doit contenir un <svg>. Empêche un fichier renommé .svg.
            const base64 = result.split(",")[1] ?? "";
            let decoded = "";
            try {
                decoded = atob(base64);
            } catch {
                decoded = "";
            }
            if (!/<svg[\s>]/i.test(decoded)) {
                input.value = "";
                showInvalidSvgError();
                return;
            }
            // Purge XSS du contenu SVG (scripts, on*, javascript:) AVANT stockage :
            // la validation <svg> seule laisserait passer `<svg onload=...>`. On
            // ré-encode le SVG nettoyé en data URL base64 — jamais le brut.
            const sanitized = sanitizeSvg(decoded);
            if (sanitized === null) {
                input.value = "";
                showInvalidSvgError();
                return;
            }
            // Encodage base64 UTF-8 safe sans `unescape` (déprécié) : on
            // percent-encode puis on reconstruit la chaîne binaire Latin-1
            // (chaque octet ≤ 0xFF, donc btoa ne lève pas). Le try/catch reste
            // une garde défensive : tout échec d'encodage = upload rejeté plutôt
            // que stockage d'une valeur corrompue.
            let safeDataUrl: string;
            try {
                const utf8Binary = encodeURIComponent(sanitized).replace(
                    /%([0-9A-F]{2})/g,
                    (_m, hex) => String.fromCharCode(parseInt(hex, 16))
                );
                safeDataUrl = `data:${CATEGORY_SVG_MIME_TYPE};base64,${btoa(
                    utf8Binary
                )}`;
            } catch {
                input.value = "";
                showInvalidSvgError();
                return;
            }
            setCategoryDisplay({
                categoryDisplaySvgBorder: safeDataUrl,
                categoryDisplaySvgBorderName: file.name,
            });
            persistOrderTaking({
                categoryDisplaySvgBorder: safeDataUrl,
                categoryDisplaySvgBorderName: file.name,
            });
            // Reset only after the async read completes, so re-selecting the
            // same file fires onChange again without aborting the read.
            input.value = "";
        };
        reader.onerror = () => {
            // Lecture échouée (fichier corrompu/illisible) : on ne stocke rien
            // et on réinitialise l'input pour permettre une nouvelle tentative.
            reader.abort();
            input.value = "";
            showInvalidSvgError();
        };
        reader.readAsDataURL(file);
    }

    function handleBorderSvgDelete(): void {
        setCategoryDisplay({
            categoryDisplaySvgBorder: "",
            categoryDisplaySvgBorderName: "",
        });
        persistOrderTaking({
            categoryDisplaySvgBorder: "",
            categoryDisplaySvgBorderName: "",
        });
    }

    const isBorderColorVisible =
        categoryDisplayBorderStyle === CATEGORY_BORDER_STYLE_UNI;

    const isSvgBorder =
        categoryDisplayBorderStyle === CATEGORY_BORDER_STYLE_SVG;

    return (
        <div data-testid="order-taking-menu-mode-2-settings">
            <div className="py-2 d-flex align-items-center">
                <div style={{ flex: 1 }}>{t("Selection background color")}</div>
                <div style={{ flex: 1 }}>
                    <ColorSwatch
                        color={resolvePrimary(categoryDisplayBackgroundColor)}
                        onChange={handleBackgroundColorChange}
                        testId="order-taking-menu-mode-2-bg-color-swatch"
                    />
                </div>
            </div>

            <div className="py-2 d-flex align-items-center">
                <div style={{ flex: 1 }}>{t("Background opacity")}</div>
                <div style={{ flex: 1 }}>
                    <ReactSwitch
                        uncheckedIcon={false}
                        checkedIcon={false}
                        handleDiameter={26}
                        offColor="#f7b4b8"
                        offHandleColor="#E30613"
                        checked={
                            categoryDisplayBackgroundOpacity >
                            CATEGORY_OPACITY_MIN
                        }
                        onChange={handleBackgroundToggle}
                        onColor="#c2eddd"
                        onHandleColor="#34C38F"
                        width={50}
                        height={20}
                        aria-label={t("Background opacity")}
                        data-testid="order-taking-menu-mode-2-opacity-switch"
                    />
                </div>
            </div>

            <div className="py-2 d-flex align-items-center">
                <div style={{ flex: 1 }}>{t("Category title color")}</div>
                <div style={{ flex: 1 }}>
                    <ColorSwatch
                        color={resolvePrimary(categoryDisplaySelectedTextColor)}
                        onChange={handleSelectedTextColorChange}
                        testId="order-taking-menu-mode-2-text-color-swatch"
                    />
                </div>
            </div>

            <div className="py-2 d-flex align-items-start">
                <div style={{ flex: 1 }} id={borderStyleLabelId}>
                    {t("Border style")}
                </div>
                <div
                    className="d-flex flex-column align-items-start"
                    style={{ flex: 1, gap: "6px" }}
                    role="radiogroup"
                    aria-labelledby={borderStyleLabelId}
                >
                    <label
                        className="d-flex align-items-center mb-0"
                        style={{ cursor: "pointer" }}
                    >
                        <input
                            type="radio"
                            name="categoryDisplayBorderStyle"
                            data-testid="order-taking-menu-mode-2-border-style-none-radio"
                            checked={
                                categoryDisplayBorderStyle ===
                                CATEGORY_BORDER_STYLE_NONE
                            }
                            onChange={() =>
                                handleBorderStyleChange(
                                    CATEGORY_BORDER_STYLE_NONE
                                )
                            }
                            style={BORDER_STYLE_RADIO_STYLE}
                        />
                        {t("none")}
                    </label>
                    <label
                        className="d-flex align-items-center mb-0"
                        style={{ cursor: "pointer" }}
                    >
                        <input
                            type="radio"
                            name="categoryDisplayBorderStyle"
                            data-testid="order-taking-menu-mode-2-border-style-uni-radio"
                            checked={
                                categoryDisplayBorderStyle ===
                                CATEGORY_BORDER_STYLE_UNI
                            }
                            onChange={() =>
                                handleBorderStyleChange(
                                    CATEGORY_BORDER_STYLE_UNI
                                )
                            }
                            style={BORDER_STYLE_RADIO_STYLE}
                        />
                        {t("Solid color")}
                    </label>
                    <label
                        className="d-flex align-items-center mb-0"
                        style={{ cursor: "pointer" }}
                    >
                        <input
                            type="radio"
                            name="categoryDisplayBorderStyle"
                            data-testid="order-taking-menu-mode-2-border-style-svg-radio"
                            checked={
                                categoryDisplayBorderStyle ===
                                CATEGORY_BORDER_STYLE_SVG
                            }
                            onChange={() =>
                                handleBorderStyleChange(
                                    CATEGORY_BORDER_STYLE_SVG
                                )
                            }
                            style={BORDER_STYLE_RADIO_STYLE}
                        />
                        {t("SVG")}
                    </label>
                </div>
            </div>

            {isBorderColorVisible ? (
                <div className="py-2 d-flex align-items-center">
                    <div style={{ flex: 1 }}>{t("Border color")}</div>
                    <div style={{ flex: 1 }}>
                        <ColorSwatch
                            color={resolvePrimary(categoryDisplayBorderColor)}
                            onChange={handleBorderColorChange}
                            testId="order-taking-menu-mode-2-border-color-swatch"
                        />
                    </div>
                </div>
            ) : null}

            {isSvgBorder ? (
                <div className="py-2 d-flex justify-content-between align-items-center">
                    <div>
                        <div>{t("Selected category frame")}</div>
                        <div style={{ color: "#B7B7B7", fontSize: "13px" }}>
                            {t("Insert an SVG frame for the selected category")}
                        </div>
                    </div>
                    <div
                        className="d-flex align-items-center"
                        style={{ gap: "8px" }}
                    >
                        <button
                            type="button"
                            disabled={categoryDisplaySvgBorder === ""}
                            onClick={handleBorderSvgDelete}
                            aria-label={t("Delete")}
                            data-testid="order-taking-menu-mode-2-border-svg-delete-btn"
                            style={ICON_BUTTON_STYLE}
                        >
                            <TrashIcon
                                style={{ fill: "red" }}
                                height={20}
                                width={20}
                            />
                        </button>
                        <label className="m-0" style={ICON_BUTTON_STYLE}>
                            <BiImageAdd
                                size={22}
                                color="#6c757d"
                                aria-hidden="true"
                            />
                            <input
                                type="file"
                                accept={CATEGORY_SVG_MIME_TYPE}
                                onChange={handleBorderSvgUpload}
                                aria-label={t("Upload SVG")}
                                data-testid="order-taking-menu-mode-2-border-svg-input"
                                style={{ display: "none" }}
                            />
                        </label>
                        {categoryDisplaySvgBorderName !== "" ? (
                            <span
                                style={{ fontSize: "13px" }}
                                data-testid="order-taking-menu-mode-2-border-svg-filename"
                            >
                                {categoryDisplaySvgBorderName}
                            </span>
                        ) : null}
                    </div>
                </div>
            ) : null}
        </div>
    );
}
